import { useGetAllDepartmentsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import { useGetPendingInterviewsQuery } from '@/app/features/recruitment/evaluation/evaluation.api';
import type { PendingInterviewItem } from '@/app/features/recruitment/evaluation/evaluation.type';
import { useGetAllJobPostsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import EmptyState from '@/components/EmptyState';
import ViewToggler from '@/components/ViewToggler';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters, useSelectionOptions } from '@/hooks';
import { ChevronDownIcon, SearchIcon, TaskDaily01Icon } from '@/icons';
import { cn } from '@/lib/cn';
import { Col, Flex, Input, Pagination, Row, Select } from 'antd';
import { useState } from 'react';
import EmpCard from './_components/EvAssessment/EmpCard';
import EmpCardSkeleton from './_components/EvAssessment/EmpCardSkeleton';
import EvaluateManualInterview from './_components/EvInterview/EvaluateManualInterview';
import ViewEvaluatedMarks from './_components/EvInterview/ViewEvaluatedMarks';

const EvInterview = () => {
  const [viewMode, setViewMode] = useState('grid');

  const {
    skip,
    page,
    limit,
    filters,
    keyword,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useAppFilters(FILTER_KEYS.EV_INTERVIEW, { defaultLimit: 12 });

  const [jobOptions] = useSelectionOptions(useGetAllJobPostsQuery, {
    labelKey: 'job_title',
    valueKey: 'id',
  });

  const [departmentOptions] = useSelectionOptions(useGetAllDepartmentsQuery, {
    labelKey: 'name',
    valueKey: 'id',
  });

  const {
    isLoading,
    isFetching,
    data: interviewsData,
  } = useGetPendingInterviewsQuery({
    skip,
    limit,
    ...filters,
    search: keyword,
  });

  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.EV_INTERVIEW,
  );

  const pendingInterviews = interviewsData?.data?.data || [];
  const total = interviewsData?.data?.meta?.total || 0;
  
  return (
    <div>
      <Flex
        align="center"
        justify="space-between"
        className="flex-wrap lg:flex-nowrap gap-4 my-6"
      >
        <ViewToggler disabled="list" mode={viewMode} setMode={setViewMode} />
        <Flex align="center" gap={16} className="flex-wrap lg:flex-nowrap">
          <Input
            size="large"
            placeholder="Search"
            onChange={handleSearch}
            style={{ minWidth: 250 }}
            prefix={<SearchIcon size={20} className="text-white" />}
          />
          <Select
            showSearch
            optionFilterProp="label"
            allowClear
            size="large"
            placement="bottomRight"
            placeholder="Job Title"
            options={jobOptions}
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(val: string) => handleFilter('job_id', val)}
          />
          <Select
            allowClear
            size="large"
            placeholder="Department"
            placement="bottomRight"
            options={departmentOptions}
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(val: string) => handleFilter('department_id', val)}
          />
          <Select
            allowClear
            size="large"
            placeholder="Status"
            placement="bottomRight"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(val: string) => handleFilter('interview_status', val)}
            options={[
              { label: 'In Progress', value: 'in_progress' },
              { label: 'Passed', value: 'passed' },
              { label: 'Failed', value: 'failed' },
              { label: 'No Show', value: 'no_show' },
            ]}
          />
        </Flex>
      </Flex>

      <Row gutter={[24, 24]} className="mb-6">
        {isLoading || isFetching ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <Col key={idx} xs={24} md={12} lg={8} xl={6}>
              <EmpCardSkeleton />
            </Col>
          ))
        ) : (
          <>
            {pendingInterviews.map((item: PendingInterviewItem) => {
              const isEvaluated = item.is_evaluated;
              return (
                <Col key={item.interview_id} xs={24} md={12} lg={8} xl={6}>
                  <EmpCard
                    item={{
                      name: item.applicant_name,
                      photo: item.applicant_photo,
                      designation: item.job_title,
                      department: item.department_name,
                      assessment_status: item.interview_status,
                    }}
                    interviewType={item.interview_type as 'ai' | 'manual'}
                    classNames={{ titleWrapper: 'items-start' }}
                    actionRender={
                      <button
                        onClick={() => {
                          handleOpen({
                            data: {
                              id: item.interview_id,
                              isEvaluated,
                              interview_type: item.interview_type,
                              applicantName: item.applicant_name,
                            },
                          });
                        }}
                        className={cn(
                          'text-theme-primary font-medium underline underline-offset-4',
                          // isEvaluated && 'cursor-auto opacity-50',
                        )}
                      >
                        {isEvaluated || item.ai_interview
                          ? 'See Marks'
                          : 'Evaluate'}
                      </button>
                    }
                  />
                </Col>
              );
            })}
            {pendingInterviews.length === 0 && (
              <Col span={24}>
                <EmptyState
                  icon={<TaskDaily01Icon size={40} />}
                  title="No Pending Interviews"
                  description="All interviews have been evaluated, or none have been scheduled for you yet."
                />
              </Col>
            )}
          </>
        )}
      </Row>

      {total > limit && (
        <Flex justify="end" className="mt-4">
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={handlePagination}
            showSizeChanger={false}
          />
        </Flex>
      )}

      <EvaluateManualInterview
        isEvaluated={false}
        open={config.open && config.data?.interview_type === 'manual' && !config.data?.isEvaluated}
        handleClose={handleClose}
        id={config.data?.id}
        applicantName={config.data?.applicantName}
      />

      <ViewEvaluatedMarks
        open={
          config.open &&
          (config.data?.interview_type === 'ai' ||
            (config.data?.interview_type === 'manual' && config.data?.isEvaluated))
        }
        handleClose={handleClose}
        id={config.data?.id}
        applicantName={config.data?.applicantName}
        interviewType={config.data?.interview_type}
      />
    </div>
  );
};

export default EvInterview;
